import { get } from 'lodash-es';
import semverGreaterThanOrEqual from 'semver/functions/gte';

import { ERR, ApplicationError } from '@shared/application-error';
import { AppConfig } from './app-config';
import type { NativeAppEvent } from './types';

/**
 * TODO: Throw error if method is not defined and handle for every method
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sendIOSWebviewEvent(event: string, details?: any) {
  if (get(window, `webkit.messageHandlers.${event}`)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.webkit.messageHandlers[event].postMessage(details);
    return true;
  }
  return false;
}

export const IOSWebview = {
  /**
   * @description Method to open link in webview
   *
   * For example, when we need to open some link in webview, without navigating to other apps, e.g browser
   */
  openLink(link: string) {
    sendIOSWebviewEvent('openLink', link);
  },
  /**
   * @description Sometimes we need to open a link in a browser, not in webview, e.g WhatsApp
   *
   * Method added in 3.6.0 version of the iOS app
   */
  openLinkInBrowser(link: string) {
    if (
      AppConfig.version &&
      semverGreaterThanOrEqual(AppConfig.version, '3.6.0')
    ) {
      const hasMethod = sendIOSWebviewEvent('openLinkInBrowser', link);

      if (!hasMethod) {
        throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
          cause: {
            appVersion: AppConfig.version,
            methodName: 'openLinkInBrowser',
          },
        });
      }
    } else {
      throw new ApplicationError(ERR.NATIVE_API_UNSUPPORTED_VERSION, {
        scopeContext: {
          appVersion: AppConfig.version,
          methodName: 'openLinkInBrowser',
        },
      });
    }
  },
  goBackToMenu() {
    sendIOSWebviewEvent('goBackToMenu');
  },
  sendAnalyticsEvent(event: NativeAppEvent) {
    const hasMethod = sendIOSWebviewEvent(
      'sendAnalyticsEvent',
      JSON.stringify(event),
    );

    if (!hasMethod) {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: { methodName: 'sendAnalyticsEvent' },
      });
    }
  },
  sendAnalyticEventToAmplitude(event: NativeAppEvent) {
    const hasMethod = sendIOSWebviewEvent(
      'sendAnalyticEventToAmplitude',
      JSON.stringify(event),
    );

    if (!hasMethod) {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: { methodName: 'sendAnalyticEventToAmplitude' },
      });
    }
  },
  requestAppConfig() {
    sendIOSWebviewEvent('requestAppConfig');
  },
  requestABTestParameterValue(keys: Array<string>) {
    const hasMethod = sendIOSWebviewEvent(
      'requestABTestParameterValue',
      JSON.stringify(keys),
    );

    if (!hasMethod) {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'requestABTestParameterValue',
        },
      });
    }
  },
  onCreated() {
    const hasMethod = sendIOSWebviewEvent('onCreated');

    if (!hasMethod) {
      throw new ApplicationError(ERR.NATIVE_API_UNIMPLEMENTED_METHOD, {
        cause: {
          appVersion: AppConfig.version,
          methodName: 'onCreated',
        },
      });
    }
  },
};
